import type { ReactNode } from 'react';

import { cn } from '@/lib/cn';

export interface CardProps {
  className?: string;
  children: ReactNode;
  /** Adds a hover lift — only for cards that are themselves interactive. */
  interactive?: boolean;
  highlighted?: boolean;
  /**
   * Drops the built-in padding for cards that manage their own layout (a
   * full-bleed image, an overflowing table). A prop rather than a `p-0` class
   * override, because utility precedence is source order in the generated
   * stylesheet, not the order they appear in `className`.
   */
  flush?: boolean;
}

export function Card({ className, children, interactive, highlighted, flush }: CardProps) {
  return (
    <div
      className={cn(
        'border-line bg-surface/70 relative rounded-3xl border backdrop-blur-sm',
        !flush && 'p-6 sm:p-8',
        'shadow-[0_24px_70px_-45px_rgb(0_0_0/0.95)]',
        interactive && 'hover:border-brand/60 hover:bg-surface-raised/80 transition duration-300 hover:-translate-y-1',
        highlighted && 'border-brand/60 shadow-[0_0_0_1px_var(--color-brand),0_24px_70px_-35px_var(--color-brand)]',
        className,
      )}
    >
      {children}
    </div>
  );
}

export function Badge({
  children,
  tone = 'brand',
  className,
}: {
  children: ReactNode;
  tone?: 'brand' | 'gold' | 'accent' | 'neutral' | 'success';
  className?: string;
}) {
  const tones: Record<string, string> = {
    brand: 'bg-brand/15 text-brand-bright border-brand/40',
    gold: 'bg-gold/15 text-gold border-gold/40',
    accent: 'bg-accent/15 text-accent border-accent/40',
    success: 'bg-success/15 text-success border-success/40',
    neutral: 'bg-white/5 text-content-muted border-line-strong',
  };

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold tracking-wider uppercase',
        tones[tone],
        className,
      )}
    >
      {children}
    </span>
  );
}
